'use strict';

export class MyAccountCampaignEdit {
    #form;
    #submitButton;
    #initialFormData;
    #initialImageCount;
    #uploadedFiles;
    #isSyncingAdditionalFiles = false;
    #activePickerInput = null;
    #ignoreUploadClicksUntil = 0;

    constructor() {
        this.#form = document.querySelector('.campaign-details.team-campaign form[data-campaign-edit-media="theme"]');
        this.#submitButton = this.#form?.querySelector('.button-update-campaign');

        if (!this.#form || !this.#submitButton) {
            return;
        }

        this.#initialFormData = new FormData(this.#form);
        this.#initialImageCount = this.#getImageCount();
        this.#uploadedFiles = new DataTransfer();

        this.#submitButton.disabled = true;

        this.#addEvents();
        this.#monitorFormChanges();
        this.#bindSubmitGuard();
    }

    #bindSubmitGuard() {
        this.#form.addEventListener('submit', () => {
            this.#form
                .querySelectorAll('.chaiathon_t_shirt_size, .gamification_t_shirt_size')
                .forEach((block) => {
                    const select = block.querySelector('select[name="t_shirt_size"]');
                    if (!select) {
                        return;
                    }
                    const hidden =
                        block.style.display === 'none' || block.offsetParent === null;
                    if (hidden) {
                        select.removeAttribute('required');
                    }
                });
        });
    }

    #bindFilePicker(button, fileInput, onSelected) {
        if (!button || !fileInput || button.dataset.campaignUploadBound === '1') {
            return;
        }

        button.dataset.campaignUploadBound = '1';

        const openPicker = (event) => {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            if (Date.now() < this.#ignoreUploadClicksUntil) {
                return;
            }

            if (this.#activePickerInput === fileInput) {
                return;
            }

            this.#activePickerInput = fileInput;

            const releasePicker = () => {
                this.#activePickerInput = null;
                this.#ignoreUploadClicksUntil = Date.now() + 500;
            };

            fileInput.addEventListener('change', releasePicker, {once: true});
            fileInput.addEventListener('cancel', releasePicker, {once: true});

            fileInput.click();
        };

        button.addEventListener('click', openPicker, true);

        if (fileInput.dataset.campaignChangeBound === '1') {
            return;
        }

        fileInput.dataset.campaignChangeBound = '1';
        fileInput.addEventListener('change', (event) => {
            onSelected(event);
        });
    }

    #addEvents() {
        const bannerInput = this.#form.querySelector('input.banner-image[name="banner_image"]');
        const bannerUploadButton = this.#form.querySelector('.campaign-banner-upload-button');

        this.#bindFilePicker(bannerUploadButton, bannerInput, () => {
            const file = bannerInput?.files?.[0];
            if (!file) {
                return;
            }

            this.#updateBannerPreview(file);
            this.#checkFormState();
        });

        const featuredInput = this.#form.querySelector('input.featured-image[name="featured_image"]');
        const featuredUploadButton = this.#form.querySelector('.campaign-featured-upload-button');

        this.#bindFilePicker(featuredUploadButton, featuredInput, () => {
            const file = featuredInput?.files?.[0];
            if (!file) {
                return;
            }

            this.#updateBannerPreview(
                file,
                '.chaiathon-campaign-featured-image-wrapper',
                '.featured_image_name',
                '.featured_image_metadata'
            );
            this.#checkFormState();
        });

        const uploadButton = this.#form.querySelector('.campaign-additional-upload-button');
        const fileInput = this.#form.querySelector('#additional_image');
        const imageContainer = this.#form.querySelector('.additional-images-container');

        if (!uploadButton || !fileInput || !imageContainer) {
            return;
        }

        this.#bindFilePicker(uploadButton, fileInput, (event) => {
            if (this.#isSyncingAdditionalFiles) {
                return;
            }

            const files = event.target.files;
            if (!files?.length) {
                return;
            }

            const added = [];

            for (const file of files) {
                if (!file.type.startsWith('image/')) {
                    continue;
                }

                const duplicate = Array.from(this.#uploadedFiles.files).some(
                    (existing) =>
                        existing.name === file.name
                        && existing.size === file.size
                        && existing.lastModified === file.lastModified
                );
                if (duplicate) {
                    continue;
                }

                this.#uploadedFiles.items.add(file);
                added.push(file);
            }

            added.forEach((file) => {
                this.#appendAdditionalImagePreview(imageContainer, file);
            });

            this.#isSyncingAdditionalFiles = true;
            fileInput.files = this.#uploadedFiles.files;
            this.#isSyncingAdditionalFiles = false;
            this.#checkFormState();
        });

        imageContainer.addEventListener('click', (e) => {
            const removeButton = e.target.closest('.remove-additional-image-button');
            if (!removeButton) {
                return;
            }

            e.preventDefault();
            const wrapper = removeButton.closest('.image-wrapper');
            if (!wrapper) {
                return;
            }

            this.#removeAdditionalImage(wrapper, imageContainer, fileInput);
            this.#checkFormState();
        });
    }

    #appendAdditionalImagePreview(imageContainer, file) {
        const reader = new FileReader();
        reader.onload = (e) => {
            const wrapper = document.createElement('div');
            wrapper.classList.add('image-wrapper');
            wrapper.dataset.newUpload = '1';

            const img = document.createElement('img');
            img.src = e.target.result;
            img.alt = file.name;

            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.className = 'delete-btn remove-additional-image-button';
            removeBtn.setAttribute('aria-label', 'Remove image');
            removeBtn.textContent = 'X';

            wrapper.append(img, removeBtn);
            imageContainer.appendChild(wrapper);
        };
        reader.readAsDataURL(file);
    }

    #removeAdditionalImage(wrapper, imageContainer, fileInput) {
        const existingImageId = wrapper.dataset.id;

        if (existingImageId) {
            const removedInput = this.#form.querySelector('#removedImagesInput');
            if (removedInput) {
                const ids = removedInput.value
                    ? removedInput.value.split(',').filter(Boolean)
                    : [];
                if (!ids.includes(existingImageId)) {
                    ids.push(existingImageId);
                    removedInput.value = ids.join(',');
                }
            }
            wrapper.querySelector('input[name="existing_images[]"]')?.remove();
        } else if (wrapper.dataset.newUpload === '1') {
            const newWrappers = Array.from(
                imageContainer.querySelectorAll('.image-wrapper[data-new-upload="1"]')
            );
            const fileIndex = newWrappers.indexOf(wrapper);

            if (fileIndex >= 0) {
                const newDataTransfer = new DataTransfer();
                Array.from(this.#uploadedFiles.files).forEach((file, index) => {
                    if (index !== fileIndex) {
                        newDataTransfer.items.add(file);
                    }
                });
                this.#uploadedFiles = newDataTransfer;
                this.#isSyncingAdditionalFiles = true;
                fileInput.files = this.#uploadedFiles.files;
                this.#isSyncingAdditionalFiles = false;
            }
        }

        wrapper.remove();
    }

    #monitorFormChanges() {
        const inputs = this.#form.querySelectorAll('input, textarea, select');
        inputs.forEach((input) => {
            input.addEventListener('input', () => this.#checkFormState());
            input.addEventListener('change', () => this.#checkFormState());
        });

        if (typeof tinymce !== 'undefined') {
            tinymce.on('AddEditor', (event) => {
                if (event.editor.id === 'campaign_message') {
                    const editor = event.editor;

                    editor.on('change input keyup', () => {
                        editor.save();
                        this.#checkFormState();
                    });
                }
            });
        }
    }

    #getImageCount() {
        return this.#form.querySelectorAll('.additional-images-container .image-wrapper').length;
    }

    #checkFormState() {
        const currentFormData = new FormData(this.#form);
        let changed = false;

        for (const [key, value] of currentFormData.entries()) {
            const original = this.#initialFormData.get(key) || '';
            if (value !== original) {
                changed = true;
                break;
            }
        }

        const currentImageCount = this.#getImageCount();
        if (currentImageCount !== this.#initialImageCount) {
            changed = true;
        }

        const removedImages = this.#form.querySelector('#removedImagesInput')?.value?.trim();
        if (removedImages) {
            changed = true;
        }

        const bannerInput = this.#form.querySelector('.banner-image');
        if (bannerInput?.files?.length) {
            changed = true;
        }

        const featuredInput = this.#form.querySelector('.featured-image');
        if (featuredInput?.files?.length) {
            changed = true;
        }

        if (this.#uploadedFiles.files.length) {
            changed = true;
        }

        this.#submitButton.disabled = !changed;
    }

    #updateBannerPreview(
        file,
        wrapperSelector = '.chaiathon-campaign-image-wrapper',
        nameSelector = '.bannered_image_name',
        metaSelector = '.bannered_image_metadata'
    ) {
        const wrapper = this.#form.querySelector(wrapperSelector);
        const imageWrap = wrapper?.querySelector('.campaign_image');

        if (!wrapper || !imageWrap || !file.type.startsWith('image/')) {
            return;
        }

        const nameEl = wrapper.querySelector(nameSelector);
        const metaEl = wrapper.querySelector(metaSelector);

        imageWrap.querySelectorAll('img').forEach((img, index) => {
            if (index > 0) {
                if (img.dataset.blobUrl) {
                    URL.revokeObjectURL(img.dataset.blobUrl);
                }
                img.remove();
            }
        });

        let img = imageWrap.querySelector('img');
        if (!img) {
            img = document.createElement('img');
            imageWrap.appendChild(img);
        }

        if (img.dataset.blobUrl) {
            URL.revokeObjectURL(img.dataset.blobUrl);
        }

        const blobUrl = URL.createObjectURL(file);
        img.dataset.blobUrl = blobUrl;
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.removeAttribute('width');
        img.removeAttribute('height');
        img.className = '';
        img.src = blobUrl;
        img.alt = file.name;

        wrapper.style.display = '';
        wrapper.hidden = false;

        if (nameEl) {
            nameEl.textContent = file.name;
        }

        if (metaEl) {
            metaEl.hidden = false;
            metaEl.textContent = `${this.#formatFileSize(file.size)}`;

            const dimensionsProbe = new Image();
            dimensionsProbe.onload = () => {
                metaEl.textContent = `${dimensionsProbe.naturalWidth}x${dimensionsProbe.naturalHeight} | ${this.#formatFileSize(file.size)}`;
            };
            dimensionsProbe.src = blobUrl;
        }
    }

    #formatFileSize(bytes) {
        if (bytes < 1024) {
            return `${bytes} B`;
        }

        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }

        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
}
